import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  Grid,
  Select,
  Switch,
  Text,
} from '@chakra-ui/react'
import {
  BadRequestError,
  EditOrganizationUnit,
  ModelState,
  OrganizationUnit,
  OrganizationUnitProperties,
} from 'BrokerTsLib/src/dtos'
import {
  getOrganizationUnitTypeName,
  OrganizationUnitType,
} from 'brokertslib/src/enums'
import { useGetAcquisitionNumbers } from 'brokertslib/src/hooks/useOrganizationUnit'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import ErrorSummary from '@/components/ErrorSummary'
import { FormGroup } from '@/components/FormGroup'
import { FormLabel } from '@/components/FormLabel'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '@/components/FormSection'
import { FormSpacer } from '@/components/FormSpacer'
import { InputGroup } from '@/components/InputGroup'
import { PhoneInputGroup } from '@/components/PhoneInputGroup'
import { SwitchControl } from '@/components/SwitchControl'
import { TextareaGroup } from '@/components/TextareaGroup'
import { useFindAddress } from '@/hooks'
import { useGetAres } from '@/hooks/useUser'
import { ArrowLeft, Cloud } from '@/icons'
import { AxiosError, AxiosResponse } from 'axios'
import { setModelStateErrors } from 'brokertslib/src/Utils/formModelState'

interface OrganizationUnitEditFormProps {
  organization: OrganizationUnit
  properties: OrganizationUnitProperties
  onSubmit: (
    values: EditOrganizationUnit,
  ) => Promise<AxiosResponse | AxiosError>
  possibleTypes: OrganizationUnitType[]
  isCreate?: boolean
}

export const OrganizationEditForm = ({
  organization,
  properties,
  onSubmit,
  possibleTypes,
  isCreate,
}: OrganizationUnitEditFormProps) => {
  const navigate = useNavigate()
  const methods = useForm<EditOrganizationUnit>({
    defaultValues: {
      ...organization,
      properties: { ...properties },
      acquisitionNumbers: [],
    },
    mode: 'onSubmit',
  })
  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setError,
    setValue,
    trigger,
  } = methods

  useGetAcquisitionNumbers(
    organization.id,
    organization.superiorOrganizationUnitId,
    {
      onSuccess: (data) => {
        const sortedData = data.data.sort((a, b) =>
          a.insurerName.localeCompare(b.insurerName),
        )

        reset((value) => ({ ...value, acquisitionNumbers: sortedData }))
      },
    },
  )

  const { fields } = useFieldArray({
    name: 'acquisitionNumbers',
    control,
  })

  const acquisitionNumbers = watch('acquisitionNumbers')

  const submitHandler = (data: EditOrganizationUnit) =>
    onSubmit(data).then((value) => {
      const e = value as AxiosError
      if (e.response?.status === 400)
        setModelStateErrors(
          setError,
          new BadRequestError(
            e.response.data as ModelState<EditOrganizationUnit>,
          ),
        )
    })

  const ico = watch('ico')

  const {
    data: aresData,
    refetch: refetchAres,
    isLoading: isAresLoading,
  } = useGetAres(ico, {
    enabled: false,
    onSuccess: (data) => {
      setValue('name', data.data.nazev)
    },
  })

  const ulice = aresData?.data.adresaUlice
    ? aresData?.data.adresaUlice
    : aresData?.data.adresaCastObce
      ? aresData?.data.adresaCastObce
      : aresData?.data.adresaObec

  const { isLoading: isRuianLoading } = useFindAddress(
    ulice!,
    aresData?.data.adresaCislo!,
    {
      enabled: !!ulice && !!aresData?.data.adresaCislo,
      onSuccess: (data) => {
        if (data?.data) {
          const pscFiltered = data?.data.filter(
            (d) => d.psc == aresData?.data.adresaPsc,
          )
          if (pscFiltered.length == 1) {
            const item = pscFiltered[0]
            setValue('addressStreet', item.ulice ?? '')
            let cp: string = item.cisloDomovni ?? ''
            if (item.cisloOrientacni) {
              cp = cp + '/' + item.cisloOrientacni
            }
            setValue('addressStreetNumber', cp)
            setValue('addressPostCode', item.psc)
            let obec = item.obec
            if (item.castObce && item.castObce != item.obec) {
              obec = obec + ' - ' + item.castObce
            }
            setValue('addressCity', obec)

            trigger('addressStreet')
            trigger('addressStreetNumber')
            trigger('addressPostCode')
            trigger('addressCity')
          }
        }
      },
    },
  )
  const isAddressLoading = isRuianLoading || isAresLoading

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <input type="hidden" {...register('id')} />

        <FormSection>
          <FormSectionHeader>
            <FormSectionTitle>Informace o organizaci</FormSectionTitle>

            <FormSectionDescription>
              Základní informace o organizaci.
            </FormSectionDescription>
          </FormSectionHeader>

          <FormSectionBody>
            <FormGroup sx={{ columnGap: 4, w: 'full' }}>
              <FormControl isRequired isInvalid={!!errors.type}>
                <FormLabel>Typ organizace</FormLabel>

                <Select
                  {...register('type', {
                    valueAsNumber: true,
                    validate: (value) =>
                      !value && value !== 0 ? 'Vyberte typ organizace' : true,
                  })}
                >
                  <option value="">Vyberte typ organizace</option>
                  {possibleTypes.map((type) => (
                    <option key={type} value={type}>
                      {getOrganizationUnitTypeName(type)}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>
                  {errors.type && errors.type.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl flexGrow={1} isRequired isInvalid={!!errors.name}>
                <FormLabel htmlFor="name">Název</FormLabel>
                <InputGroup
                  id="name"
                  placeholder="Název organizace"
                  errors={errors.name}
                  {...register('name', {
                    required: 'Zadejte název organizace',
                    maxLength: 256,
                  })}
                  sx={{ w: 'full' }}
                />
              </FormControl>
            </FormGroup>

            <FormControl variant="fullWidth">
              <FormLabel>Popis</FormLabel>

              <TextareaGroup
                placeholder="Popis organizace"
                {...register('description', { maxLength: 2000 })}
              ></TextareaGroup>
            </FormControl>

            <FormGroup columnGap={4}>
              <FormControl>
                <FormLabel>IČO</FormLabel>

                <Flex
                  justifyContent="start"
                  alignItems="center"
                  gap={4}
                  flexWrap="wrap"
                >
                  <InputGroup
                    placeholder="Identifikační číslo firmy"
                    pattern="\d+"
                    {...register('ico', { maxLength: 20 })}
                  />
                  <Button
                    variant="outline"
                    isLoading={isAddressLoading}
                    isDisabled={
                      isAddressLoading || ico?.length < 8 || ico?.length > 10
                    }
                    onClick={() => refetchAres()}
                  >
                    <Cloud color="secondary.500" />
                    Načíst údaje
                  </Button>
                  {aresData?.data?.errorMessage && (
                    <Text color="red.600">{aresData.data.errorMessage}</Text>
                  )}
                </Flex>
              </FormControl>
            </FormGroup>

            <FormSpacer />

            <FormControl>
              <FormLabel>Kód ČNB</FormLabel>

              <InputGroup
                placeholder="Kód přidělený ČNB"
                {...register('nationalBankLicenseCode', { maxLength: 256 })}
              />
            </FormControl>

            <FormSpacer />

            <FormGroup>
              <FormControl>
                <FormLabel>Ulice</FormLabel>

                <InputGroup
                  placeholder="Např. Dlouhá"
                  htmlSize={24}
                  {...register('addressStreet', { maxLength: 100 })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Č.p. a č.o.</FormLabel>

                <InputGroup
                  placeholder="Např. 123"
                  htmlSize={6}
                  {...register('addressStreetNumber', { maxLength: 10 })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>PSČ</FormLabel>

                <InputGroup
                  placeholder="Např. 110 00"
                  htmlSize={6}
                  pattern="[\d\s]+"
                  {...register('addressPostCode', { maxLength: 20 })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Město</FormLabel>

                <InputGroup
                  placeholder="Např. Praha"
                  htmlSize={12}
                  {...register('addressCity', { maxLength: 100 })}
                />
              </FormControl>
            </FormGroup>

            {!isCreate && (
              <>
                <FormSpacer />
                <FormControl>
                  <FormLabel>Aktivní</FormLabel>
                  <SwitchControl pt={2}>
                    <FormLabel htmlFor="organizationUnitValid" variant="switch">
                      Ne
                    </FormLabel>
                    <Switch
                      id="organizationUnitValid"
                      size="md"
                      {...register('valid')}
                    />
                    <FormLabel htmlFor="organizationUnitValid" variant="switch">
                      Ano
                    </FormLabel>
                  </SwitchControl>
                </FormControl>
              </>
            )}
          </FormSectionBody>
        </FormSection>

        <FormSection>
          <FormSectionHeader>
            <FormSectionTitle>Kontaktní osoba</FormSectionTitle>

            <FormSectionDescription>
              Zadejte kontakty na kontaktní osobu ogranizace.
            </FormSectionDescription>
          </FormSectionHeader>

          <FormSectionBody>
            <FormControl>
              <FormLabel>Celé jméno</FormLabel>

              <InputGroup
                placeholder="Např. Jan Novák"
                {...register('contactName', { maxLength: 100 })}
              />
            </FormControl>

            <FormGroup>
              <FormControl>
                <FormLabel>Telefon</FormLabel>

                <PhoneInputGroup input={register('phone', { maxLength: 20 })} />
              </FormControl>

              <FormControl>
                <FormLabel>Mobilní telefon</FormLabel>

                <PhoneInputGroup
                  input={register('mobilePhone', { maxLength: 20 })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>E-mail</FormLabel>

                <InputGroup
                  type="email"
                  placeholder="Např. jan.novak@okgroup.cz"
                  {...register('email', { maxLength: 256 })}
                />
              </FormControl>
            </FormGroup>
          </FormSectionBody>
        </FormSection>

        {acquisitionNumbers?.length > 0 && (
          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Ziskatelská čísla</FormSectionTitle>

              <FormSectionDescription>
                Zadejte ziskatelská čísla u jednotlivých pojišťoven.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <Box
                sx={{
                  display: 'grid',
                  gridAutoFlow: 'column',
                  gridTemplateColumns: { base: '1fr', md: '1fr 1fr' },
                  gridTemplateRows: `repeat(${Math.round(acquisitionNumbers.length / 2)}, auto)`,
                  columnGap: 16,
                  rowGap: 4,
                }}
              >
                {fields.map((field, index) => {
                  return (
                    <FormControl key={field.id}>
                      <FormLabel htmlFor={`acquisitionNumbers.${index}.number`}>
                        {field.insurerName}
                      </FormLabel>

                      <InputGroup
                        id={`acquisitionNumbers.${index}.number`}
                        defaultValue={field.acquisitionNumber}
                        {...register(
                          `acquisitionNumbers.${index}.acquisitionNumber` as const,
                          {
                            maxLength: 256,
                          },
                        )}
                      />
                    </FormControl>
                  )
                })}
              </Box>
            </FormSectionBody>
          </FormSection>
        )}

        <FormSection>
          <FormSectionHeader>
            <FormSectionTitle>Nastavení aplikace</FormSectionTitle>

            <FormSectionDescription>
              Dostupná nastavení aplikace pro danou organizaci.
            </FormSectionDescription>
          </FormSectionHeader>

          <FormSectionBody>
            <FormControl>
              <FormLabel>Webové stránky</FormLabel>
              <InputGroup
                placeholder="Např. https://www.okgroup.cz/"
                {...register('properties.webPage')}
              />
            </FormControl>

            <FormControl variant="fullWidth">
              <FormLabel>E-mail - zápatí</FormLabel>
              <TextareaGroup {...register('properties.footerInfo')} />
            </FormControl>

            <FormControl variant="fullWidth">
              <FormLabel>Poučení subjektu - záhlaví</FormLabel>
              <TextareaGroup
                placeholder="Napište poučení subjektu, které se zobrazí v záhlaví."
                {...register('properties.informationMemorandumHeader')}
              />
            </FormControl>

            <FormControl variant="fullWidth">
              <FormLabel>Poučení subjektu - zápatí </FormLabel>
              <TextareaGroup
                placeholder="Napište poučení subjektu, které se zobrazí v zápatí."
                {...register('properties.informationMemorandumBottom')}
              />
            </FormControl>

            <FormControl variant="fullWidth">
              <FormLabel>Obchodní rejstřík</FormLabel>
              <TextareaGroup
                placeholder="Poznámka o zápisu firmy do obchodního rejstříku"
                {...register('properties.businessRegister')}
              />
            </FormControl>

            <FormGroup>
              <FormControl>
                <FormLabel>Právnická osoba</FormLabel>

                <SwitchControl>
                  <FormLabel htmlFor="businessSubject" variant="switch">
                    Ne
                  </FormLabel>
                  <Switch
                    id="businessSubject"
                    size="md"
                    {...register('properties.businessSubject')}
                  />
                  <FormLabel htmlFor="businessSubject" variant="switch">
                    Ano
                  </FormLabel>
                </SwitchControl>
              </FormControl>

              <FormControl>
                <FormLabel>Zobrazit logo OK Brokers v ZzJ</FormLabel>

                <SwitchControl>
                  <FormLabel htmlFor="showLogo" variant="switch">
                    Ne
                  </FormLabel>
                  <Switch
                    id="showLogo"
                    size="md"
                    {...register('properties.preContractDocument2ndLogo')}
                  />
                  <FormLabel htmlFor="showLogo" variant="switch">
                    Ano
                  </FormLabel>
                </SwitchControl>
              </FormControl>

              <FormControl>
                <FormLabel>Povinné vyplnění emailu pojistníka</FormLabel>

                <SwitchControl>
                  <FormLabel htmlFor="requiredInsureeEmail" variant="switch">
                    Ne
                  </FormLabel>
                  <Switch
                    id="requiredInsureeEmail"
                    size="md"
                    {...register('properties.policyHolderEmailRequired')}
                  />
                  <FormLabel htmlFor="requiredInsureeEmail" variant="switch">
                    Ano
                  </FormLabel>
                </SwitchControl>
              </FormControl>
            </FormGroup>
          </FormSectionBody>
        </FormSection>

        <FormSection>
          <FormSectionHeader>
            <FormSectionTitle>Nastavení pojištění vozidel</FormSectionTitle>
          </FormSectionHeader>
          <FormSectionBody>
            <FormControl variant="fullWidth">
              <FormLabel>Odkazy na dokumenty</FormLabel>
              <TextareaGroup
                placeholder="Odkazy na dokumenty ke stažení"
                {...register('properties.preContractDocumentLinks')}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Odkaz na IPID</FormLabel>
              <InputGroup
                placeholder="Odkaz na IPIDy"
                {...register('properties.ipidLink')}
              />
            </FormControl>
            <FormGroup>
              <FormControl>
                <FormLabel>Autobazar</FormLabel>
                <SwitchControl>
                  <FormLabel htmlFor="carInsuranceAutobazar" variant="switch">
                    Ne
                  </FormLabel>
                  <Switch
                    id="carInsuranceAutobazar"
                    size="md"
                    {...register('properties.allianzNoYearkm')}
                  />
                  <FormLabel htmlFor="carInsuranceAutobazar" variant="switch">
                    Ano
                  </FormLabel>
                </SwitchControl>
              </FormControl>
              <FormControl>
                <FormLabel>
                  Zobrazit Preferovaný způsob likvidace škod
                </FormLabel>
                <SwitchControl>
                  <FormLabel
                    htmlFor="carInsuranceShowPrefferedLiquidation"
                    variant="switch"
                  >
                    Ne
                  </FormLabel>
                  <Switch
                    id="carInsuranceShowPrefferedLiquidation"
                    size="md"
                    {...register('properties.showPreferredLiquidation')}
                  />
                  <FormLabel
                    htmlFor="carInsuranceShowPrefferedLiquidation"
                    variant="switch"
                  >
                    Ano
                  </FormLabel>
                </SwitchControl>
              </FormControl>
            </FormGroup>
          </FormSectionBody>
        </FormSection>

        <FormSection>
          <FormSectionHeader>
            <FormSectionTitle>
              Nastavení pojištění majetku a odpovědnosti
            </FormSectionTitle>
          </FormSectionHeader>
          <FormSectionBody>
            <FormControl variant="fullWidth">
              <FormLabel>Odkazy na dokumenty</FormLabel>
              <TextareaGroup
                placeholder="Odkazy na dokumenty ke stažení"
                {...register('properties.preContractDocumentLinks2')}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Odkaz na IPID</FormLabel>
              <InputGroup
                placeholder="Odkaz na IPIDy"
                {...register('properties.ipidLink2')}
              />
            </FormControl>
          </FormSectionBody>
        </FormSection>

        <FormSection>
          <FormSectionHeader>
            <FormSectionTitle>Nastavení cestovního pojištění</FormSectionTitle>
          </FormSectionHeader>
          <FormSectionBody>
            <FormControl variant="fullWidth">
              <FormLabel>Odkazy na dokumenty</FormLabel>
              <TextareaGroup
                placeholder="Odkazy na dokumenty ke stažení"
                {...register('properties.preContractDocumentLinks3')}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Odkaz na IPID</FormLabel>
              <InputGroup
                placeholder="Odkaz na IPIDy"
                {...register('properties.ipidLink3')}
              />
            </FormControl>
          </FormSectionBody>
        </FormSection>

        <FormSection>
          <FormSectionHeader>
            <FormSectionTitle>Nastavení loginů a hesel</FormSectionTitle>
            <FormSectionDescription>
              Zadejte loginy a hesla k jednotlivým systémům pojišťoven.
              <br /> Login a heslo oddělte pomocí znaku "|" bez mezer.
            </FormSectionDescription>
          </FormSectionHeader>
          <FormSectionBody>
            <FormControl>
              <FormLabel>ČSOB Pojišťovna, a. s.</FormLabel>
              <InputGroup
                placeholder="login|heslo"
                htmlSize={32}
                pattern=".+\|.+"
                {...register('properties.csobCredentials')}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Pillow pojišťovna a.s.</FormLabel>
              <InputGroup
                placeholder="login|heslo"
                htmlSize={32}
                pattern=".+\|.+"
                {...register('properties.pillowCredentials')}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Pojišťovna VZP, a.s.</FormLabel>
              <InputGroup
                placeholder="login|heslo"
                htmlSize={32}
                pattern=".+\|.+"
                {...register('properties.pVzpCredentials')}
              />
            </FormControl>
            <FormControl>
              <FormLabel>AXA ASSISTANCE CZ, s.r.o.</FormLabel>
              <InputGroup
                placeholder="login|heslo"
                htmlSize={32}
                pattern=".+\|.+"
                {...register('properties.axaAssistanceCredentials')}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Colonnade Insurance S.A.</FormLabel>
              <InputGroup
                placeholder="login|heslo|získatel Colonnade"
                htmlSize={32}
                pattern=".+\|.+\|.+"
                {...register('properties.colonnadeTravelCredentials')}
              />
            </FormControl>
            <Divider></Divider>
          </FormSectionBody>
        </FormSection>

        <ErrorSummary errors={errors} />

        <Grid
          sx={{
            width: '100%',
            gridTemplateColumns: 'auto auto',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant="ghost"
            isLoading={isSubmitting}
            type="button"
            onClick={() =>
              navigate(
                '/organization/' +
                  (organization.id ??
                    organization.superiorOrganizationUnitId ??
                    'list'),
              )
            }
          >
            <ArrowLeft color="secondary.500" />
            Zpět
          </Button>
          <Button
            colorScheme="primary"
            isLoading={isSubmitting}
            onClick={() => {
              handleSubmit(submitHandler)()
            }}
          >
            {isCreate ? 'Vytvořit organizaci' : 'Uložit'}
          </Button>
        </Grid>
      </form>
    </FormProvider>
  )
}
